import { Flag, Clock, Check, Stamp } from 'lucide-react'
import { getStatusIcon } from '@/lib/utils/ui-helpers'
import { Badge } from '@/components/ui/badge'
import { Status, Priority, Horizon, TaskType } from '@/types/enums'

/**
 * Property type for the badge
 */
export type PropertyType = 'priority' | 'horizon' | 'status' | 'taskType'

/**
 * Icon size variants for property display
 */
export type IconSize = 'sm' | 'md' | 'lg'

const iconSizeClasses: Record<IconSize, string> = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
}

/**
 * Props for the NodePropertyBadge component
 */
interface NodePropertyBadgeProps {
  /**
   * The type of property to display
   */
  type: PropertyType
  /**
   * The property value to display (Priority, Horizon, Status, TaskType enum or string)
   */
  value?: Priority | Horizon | Status | TaskType | string | null
  /**
   * Optional label to display before the property (defaults based on type)
   */
  label?: string
  /**
   * Size of the property icon (defaults to 'md')
   */
  iconSize?: IconSize
  /**
   * Additional CSS classes for the container
   */
  className?: string
  /**
   * Whether to use hover mode (icon only, text reveals on hover)
   */
  hoverMode?: boolean
}

/**
 * Get badge variant for priority levels
 */
function getPriorityBadgeVariant(
  priority: Priority | undefined | null
): 'gray' | 'red' | 'yellow' | 'green' {
  if (!priority) {
    return 'gray'
  }

  switch (priority) {
    case Priority.None:
      return 'gray'
    case Priority.High:
      return 'red'
    case Priority.Medium:
      return 'yellow'
    case Priority.Low:
      return 'green'
    default:
      return 'gray'
  }
}

/**
 * Get badge variant for horizon levels
 */
function getHorizonBadgeVariant(
  horizon: Horizon | undefined | null
): 'gray' | 'red' | 'blue' | 'purple' {
  if (!horizon) {
    return 'gray'
  }

  switch (horizon) {
    case Horizon.None:
    case Horizon.Never:
      return 'gray'
    case Horizon.Now:
      return 'red'
    case Horizon.Next:
      return 'blue'
    case Horizon.Someday:
      return 'purple'
    default:
      return 'gray'
  }
}

/**
 * Get badge variant for task types
 */
function getTaskTypeBadgeVariant(
  taskType: TaskType | string | undefined | null
): 'gray' | 'blue' | 'purple' | 'green' {
  if (!taskType) {
    return 'gray'
  }

  switch (taskType) {
    case TaskType.Task:
      return 'blue'
    case TaskType.Milestone:
      return 'purple'
    case TaskType.Approval:
      return 'green'
    default:
      return 'gray'
  }
}

/**
 * Get badge variant for status levels
 */
function getStatusBadgeVariant(
  status: Status | string | undefined | null
): 'yellow' | 'blue' | 'green' | 'gray' | 'default' {
  // Handle undefined or null status
  if (!status) {
    return 'default'
  }

  switch (status) {
    case Status.Draft:
      return 'yellow'
    case Status.ToDo:
      return 'blue'
    case Status.InProgress:
      return 'green'
    case Status.Done:
      return 'gray'
    default: {
      // Handle string values that might not match the enum exactly
      const statusStr = status.toString().toLowerCase()
      if (statusStr.includes('draft')) return 'yellow'
      if (statusStr.includes('todo') || statusStr.includes('to do')) return 'blue'
      if (statusStr.includes('progress') || statusStr.includes('in progress')) return 'green'
      if (statusStr.includes('done')) return 'gray'
      return 'default'
    }
  }
}

/**
 * Get the appropriate icon for the property type
 */
function getPropertyIcon(type: PropertyType, value: string | undefined | null, iconSize: IconSize) {
  switch (type) {
    case 'priority':
      return <Flag className={iconSizeClasses[iconSize]} />
    case 'horizon':
      return <Clock className={iconSizeClasses[iconSize]} />
    case 'status':
      return getStatusIcon(value, iconSize)
    case 'taskType':
      if (value === TaskType.Task) {
        return <Check className={iconSizeClasses[iconSize]} />
      } else if (value === TaskType.Milestone) {
        return <Flag className={iconSizeClasses[iconSize]} />
      } else if (value === TaskType.Approval) {
        return <Stamp className={iconSizeClasses[iconSize]} />
      }
      return null
    default:
      return null
  }
}

/**
 * Get the default label for the property type
 */
function getDefaultLabel(type: PropertyType): string {
  switch (type) {
    case 'priority':
      return 'Priority:'
    case 'horizon':
      return 'Horizon:'
    case 'status':
      return 'Status:'
    case 'taskType':
      return 'Type:'
    default:
      return ''
  }
}

/**
 * A unified component for displaying node properties (priority, horizon, status) with consistent styling
 *
 * @example
 * ```tsx
 * // Priority badge
 * <NodePropertyBadge type="priority" value={Priority.High} />
 *
 * // Horizon badge
 * <NodePropertyBadge type="horizon" value={Horizon.Next} />
 *
 * // Status badge
 * <NodePropertyBadge type="status" value={Status.Done} />
 *
 * // Task type badge
 * <NodePropertyBadge type="taskType" value={TaskType.Milestone} />
 *
 * // With custom label
 * <NodePropertyBadge type="priority" value="High" label="Level:" />
 *
 * // With custom styling
 * <NodePropertyBadge
 *   type="status"
 *   value={Status.ToDo}
 *   iconSize="lg"
 *   className="text-lg"
 * />
 *
 * // Hover mode (icon only, text reveals on hover)
 * <NodePropertyBadge type="priority" value={Priority.High} hoverMode />
 *
 * // Hover mode with custom label
 * <NodePropertyBadge type="status" value="In Progress" label="Progress:" hoverMode />
 * ```
 */
export function NodePropertyBadge({
  type,
  value,
  label,
  iconSize = 'md',
  className = '',
  hoverMode = false,
}: NodePropertyBadgeProps) {
  // If value is not provided, don't render anything
  if (!value) {
    return null
  }

  const defaultLabel = getDefaultLabel(type)
  const displayLabel = label !== undefined ? label : defaultLabel
  const icon = getPropertyIcon(type, value, iconSize)

  const badgeVariant = (() => {
    switch (type) {
      case 'priority':
        return getPriorityBadgeVariant(value as Priority)
      case 'horizon':
        return getHorizonBadgeVariant(value as Horizon)
      case 'status':
        return getStatusBadgeVariant(value)
      case 'taskType':
        return getTaskTypeBadgeVariant(value as TaskType)
      default:
        return 'default' as const
    }
  })()

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {displayLabel && <span className="text-sm text-muted-foreground">{displayLabel}</span>}
      {hoverMode ? (
        <Badge variant={badgeVariant} icon={icon}>
          {value}
        </Badge>
      ) : (
        <Badge variant={badgeVariant}>
          {icon && <span className="mr-1">{icon}</span>}
          <span className="font-medium text-foreground">{value}</span>
        </Badge>
      )}
    </div>
  )
}
